import { Dropdown, Menu } from 'antd';
import classnames from 'classnames';
import _ from 'lodash';
// Bypasses the shared/ui barrel: routing through it would create an import cycle, since the
// barrel also re-exports FabricJS/AIECGViewer, which imports @/entities/ecg.
import ToggleButton from '@/shared/ui/components/Buttons/toggleButton';
import { GAIN_VALUES, HP_VALUES } from '@/shared/config';
// Same import-cycle bypass as ToggleButton above: @/shared/ui/icons has no dependency on
// FabricJS/AIECGViewer, so it's safe even though the top-level @/shared/ui barrel isn't.
import { EcgToolbarArrowIconBlack as ToolbarArrowIcon } from '@/shared/ui/icons';
import { checkHasChannel } from '@/shared/lib/thumbnailConfig';
import { sizeThumbnailMenu, thumbnailChannelList } from '../model/thumbnailConfig';
import useEventThumbnailConfig from '../model/useEventThumbnailConfig';

export type EventThumbnailConfigProps = {
  isDisabled?: boolean;
  isArtifact?: boolean;
  isAfib?: boolean;
};

/**
 * Renders the configuration bar controlling how ECG event thumbnails are drawn. Reads the ECG data map, size, gain, visible channels and high-pass value plus their handlers from useEventThumbnailConfig, renders a toggle for each channel present in the loaded data with a divider before the ones past the third, and appends the size, gain and high-pass dropdowns.
 *
 * @param props - Config props carrying isDisabled and the values consumed by useEventThumbnailConfig.
 * @returns The rendered thumbnail configuration bar.
 */
const EventThumbnailConfig = ({ isDisabled, isArtifact, isAfib }: EventThumbnailConfigProps) => {
  const {
    ecgDataMap,
    size,
    gain,
    channels,
    highPass,
    onClickItemSize,
    onClickItemGain,
    onClickItemHighPass,
    onClickToggleButton,
  } = useEventThumbnailConfig({ isArtifact, isAfib });

  /**
   * Renders the thumbnail size selector for the ECG events tab. Builds a menu entry per sizeThumbnailMenu option with the current size marked selected and wires clicks to onClickItemSize.
   *
   * @returns The rendered size dropdown with its trigger button.
   */
  const renderSizeDropdown = () => {
    const items: any[] = [];
    _.forEach(sizeThumbnailMenu, (x, i) =>
      items.push({
        key: `size-${i}`,
        label: <div className={classnames(x === size ? 'selected' : '')}>{x}</div>,
        value: x,
      }),
    );
    return (
      <Dropdown
        disabled={isDisabled}
        trigger={['click']}
        overlay={<Menu onClick={onClickItemSize} className="thumbnail-size-menu" items={items} />}
      >
        <button id="ecg-events-thumbnail-size-btn" className="thumbnail-size-text">
          {`Size: ${size}`}
          <img src={ToolbarArrowIcon} alt="arrow-icon" />
        </button>
      </Dropdown>
    );
  };

  /**
   * Renders the gain selector for ECG event thumbnails. Builds a menu entry per GAIN_VALUES option labelled in mm/mV with the current gain marked selected, wires clicks to onClickItemGain, and follows the shared disabled flag of the config bar.
   *
   * @returns The rendered gain dropdown with its trigger button.
   */
  const renderGainDropdown = () => {
    const items: any[] = [];
    _.forEach(GAIN_VALUES, (x, i) =>
      items.push({
        key: `gain-${i}`,
        label: <div className={classnames(x === gain ? 'selected' : '')}>{`${x} mm/mV`}</div>,
        value: x,
      }),
    );
    return (
      <Dropdown
        disabled={isDisabled}
        trigger={['click']}
        overlay={<Menu onClick={onClickItemGain} className="thumbnail-size-menu" items={items} />}
      >
        <button id="ecg-events-change-gain-btn" className="thumbnail-size-text">
          {`Gain: ${gain}`}
          <img src={ToolbarArrowIcon} alt="arrow-icon" />
        </button>
      </Dropdown>
    );
  };

  /**
   * Renders the high-pass filter selector for ECG event thumbnails. Builds a menu entry per HP_VALUES option labelled in Hz with zero shown as No, marks the current highPass as selected, and wires clicks to onClickItemHighPass.
   *
   * @returns The rendered high-pass dropdown with its trigger button.
   */
  const renderHighPassDropdown = () => {
    const items: any[] = [];
    _.forEach(HP_VALUES, (x, i) =>
      items.push({
        key: `highpass-${i}`,
        label: (
          <div className={classnames(x === highPass ? 'selected' : '')}>
            {x !== 0 ? `${x} Hz` : 'No'}
          </div>
        ),
        value: x,
      }),
    );
    return (
      <Dropdown
        disabled={isDisabled}
        trigger={['click']}
        overlay={
          <Menu onClick={onClickItemHighPass} className="thumbnail-size-menu" items={items} />
        }
      >
        <button id="ecg-events-change-high-pass-btn" className="thumbnail-size-text">
          {`HP: ${highPass !== 0 ? highPass : 'No'}`}
          <img src={ToolbarArrowIcon} alt="arrow-icon" />
        </button>
      </Dropdown>
    );
  };

  return (
    <div className="thumbnail-config">
      <div className="channel-config">
        Thumbnail:
        {_.map(thumbnailChannelList, (channel, index) => {
          if (index < 3) {
            return (
              checkHasChannel(ecgDataMap.channels, channel) && (
                <ToggleButton
                  id={`thumbnail-channel-btn-${index}`}
                  disabled={isDisabled}
                  name={channel.label}
                  data={channel}
                  isActive={channels.includes(channel.value)}
                  onClick={onClickToggleButton}
                />
              )
            );
          }
          return (
            <>
              <div className="divider" />
              <ToggleButton
                id={`thumbnail-channel-btn-${index}`}
                disabled={isDisabled}
                name={channel.label}
                data={channel}
                isActive={channels.includes(channel.value)}
                onClick={onClickToggleButton}
              />
            </>
          );
        })}
      </div>
      <div className="size-config">{renderSizeDropdown()}</div>
      <div className="gain-config">{renderGainDropdown()}</div>
      <div className="highpass-config">{renderHighPassDropdown()}</div>
    </div>
  );
};

export default EventThumbnailConfig;
